import { c as _c } from "react/compiler-runtime";
import { IcoClipboard16, IcoCopy16, IcoPlusSmall16 } from '@onefootprint/icons';
import { Button, Card, IconButton, Toggle, Tooltip } from '@onefootprint/ui';
import { type FocusEvent, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { createInlineMarkupRegistry, EditableText } from '@/components/block-primitives';
import RequiredToggle from '@/components/required-toggle';
import useMobileLayout from '@/hooks/use-mobile-layout';
import FieldKeyErrorMessage from '../components/field-key-error';
import InputsSuggestions from '../components/suggestion/inputs-suggestions';
import { FIELD_TYPE_BADGE, InputTypeSelect } from '../components/task/schema';
import { EnumValues, OutputColumns, OutputDetail, PropertyColumns, PropertyRows } from '../components/task-editor/output-detail';
import { RemoveButton, TypeChip } from '../components/task-editor/primitives';
import ValidationAlerts from '../components/validation-alerts';
import { usePersistInputs } from '../hooks/use-inputs-save';
import { DEFAULT_FIELD_KEY, type FieldKeyError, namedFieldKeyError } from '../store/field-key';
import { useBuilder } from '../store/provider';
import { INPUT_NODE_ID, type InputField, isEnumType, isObjectType } from '../store/types';
import { availableKey, fieldRowId, padEnumValues, uid } from '../store/utils';
import { type SchemaPatch, toSchemaEditorState } from '../store/wire';
import { DocumentPane } from './document-pane';
type InputPaneProps = {
  inputs: InputField[];
};
type InputPatch = Partial<Pick<InputField, 'name' | 'description' | 'type' | 'enumValues' | 'children' | 'required'>>;
const InputPane = ({
  inputs
}: InputPaneProps) => {
  const {
    t
  } = useTranslation('agents', {
    keyPrefix: 'agentBuilder.canvas.input'
  });
  const {
    state,
    dispatch,
    isReadOnly,
    actions,
    mutateOp
  } = useBuilder();
  const persistInputs = usePersistInputs();
  const justAddedRef = useRef<string | null>(null);
  const focusFirstInput = !isReadOnly && inputs.length === 1 && inputs[0].name.trim().length === 0;
  const saveInputs = (next: InputField[]) => {
    dispatch({
      type: 'updateInputs',
      inputs: next
    });
    persistInputs(next);
  };
  const handleBlur = (e: FocusEvent<HTMLElement>) => {
    if (!e.currentTarget.contains(e.relatedTarget as Node)) persistInputs(inputs);
  };
  const addInput = () => {
    const newId = uid('input');
    justAddedRef.current = newId;
    saveInputs([...inputs, {
      id: newId,
      name: availableKey(DEFAULT_FIELD_KEY, new Set(inputs.map(input => input.name))),
      description: '',
      type: 'text',
      required: true
    }]);
  };
  const updateVaultDocumentAccess = (enabled: boolean) => {
    dispatch({
      type: 'updateVaultDocumentAccess',
      enabled
    });
    mutateOp({
      kind: 'update_vault_document_access',
      includeAllVaultDocuments: enabled
    });
  };
  const removeInput = (id: string) => {
    saveInputs(inputs.filter(input_0 => input_0.id !== id));
  };
  const updateInput = (id_0: string, patch: InputPatch) => {
    dispatch({
      type: 'updateInputs',
      inputs: inputs.map(input_1 => input_1.id === id_0 ? {
        ...input_1,
        ...patch
      } : input_1)
    });
  };
  // A text edit commits on the same blur that reaches `handleBlur`, which still holds the inputs from
  // before it, so it saves here instead of waiting for a blur that would save the old value.
  const commitInput = (id_1: string, patch_0: InputPatch) => {
    saveInputs(inputs.map(input_2 => input_2.id === id_1 ? {
      ...input_2,
      ...patch_0
    } : input_2));
  };
  return <DocumentPane id="input" onBlur={handleBlur} title={<h2 className="text-heading-2 text-primary">{t($ => $.title)}</h2>} subtitle={t($_0 => $_0.description)} contentClassName="gap-3" actions={<>
          {inputs.length > 0 && <Tooltip content={t($_1 => $_1.copy)}>
              <IconButton aria-label={t($_2 => $_2.copy)} icon={IcoCopy16} size="compact" variant="ghost" onClick={() => actions.copyInputs()} />
            </Tooltip>}
          {!isReadOnly && <Tooltip content={t($_3 => $_3.paste)}>
              <IconButton aria-label={t($_4 => $_4.paste)} icon={IcoClipboard16} size="compact" variant="ghost" onClick={() => actions.pasteInputs()} />
            </Tooltip>}
        </>}>
      <ValidationAlerts nodeId={INPUT_NODE_ID} />
      {(state.canIncludeAllVaultDocuments || state.bundle.includeAllVaultDocuments) && <Toggle checked={state.bundle.includeAllVaultDocuments ?? false} disabled={isReadOnly || !state.canIncludeAllVaultDocuments} label={t($_5 => $_5.vaultDocuments.label)} hint={state.canIncludeAllVaultDocuments ? t($_6 => $_6.vaultDocuments.hint) : t($_7 => $_7.vaultDocuments.unavailable)} onChange={updateVaultDocumentAccess} />}
      <InputsSuggestions />
      {/* The well and its add button are one group, centred like the outputs section's. */}
      <div className="flex flex-col items-center gap-2">
        {/* The well is there as soon as the page is editable, because adding the first input is what
            the empty state is for. */}
        {(!isReadOnly || inputs.length > 0) && <Card.Root>
            {inputs.length === 0 ? <Card.Empty>{t($_8 => $_8.empty)}</Card.Empty> : <div className="flex flex-col gap-1">
                {inputs.map((input_3, index) => <InputRow key={input_3.id} input={input_3} canEdit={!isReadOnly} autoEdit={focusFirstInput && index === 0 || input_3.id === justAddedRef.current} keyError={namedFieldKeyError(input_3, inputs)} onUpdate={updateInput} onCommit={commitInput} onRemove={removeInput} />)}
              </div>}
          </Card.Root>}

        {!isReadOnly && <Button onClick={addInput} prefixIcon={IcoPlusSmall16} size="compact" variant="secondary">
            {t($_9 => $_9.add)}
          </Button>}
      </div>
    </DocumentPane>;
};
type InputRowProps = {
  input: InputField;
  canEdit: boolean;
  autoEdit?: boolean;
  keyError?: FieldKeyError;
  onUpdate: (id: string, patch: InputPatch) => void;
  onCommit: (id: string, patch: InputPatch) => void;
  onRemove: (id: string) => void;
};
const inputMarkup = createInlineMarkupRegistry([]);
const InputRow = t0 => {
  const $ = _c(79);
  const {
    input,
    canEdit,
    autoEdit,
    keyError,
    onUpdate,
    onCommit,
    onRemove
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      keyPrefix: "agentBuilder.canvas.input"
    };
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const {
    t
  } = useTranslation("agents", t1);
  const isMobile = useMobileLayout();
  let t2;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = {
      keyPrefix: "agentBuilder.canvas.task.output"
    };
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const {
    t: tTable
  } = useTranslation("agents", t2);
  let t3;
  if ($[2] !== autoEdit || $[3] !== canEdit || $[4] !== input || $[5] !== isMobile || $[6] !== keyError || $[7] !== onCommit || $[8] !== onRemove || $[9] !== onUpdate || $[10] !== t || $[11] !== tTable) {
    const schemaState = toSchemaEditorState(input);
    const type = schemaState.type;
    let t4;
    if ($[13] !== input.children) {
      t4 = input.children ?? [];
      $[13] = input.children;
      $[14] = t4;
    } else {
      t4 = $[14];
    }
    const properties = t4;
    let t5;
    if ($[15] !== input.name || $[16] !== t) {
      t5 = input.name || t(_temp);
      $[15] = input.name;
      $[16] = t;
      $[17] = t5;
    } else {
      t5 = $[17];
    }
    const named = t5;
    let t6;
    if ($[18] !== input.id || $[19] !== onUpdate) {
      t6 = patch => {
        onUpdate(input.id, patch);
      };
      $[18] = input.id;
      $[19] = onUpdate;
      $[20] = t6;
    } else {
      t6 = $[20];
    }
    const updateSchema = t6;
    let t7;
    if ($[21] !== input.id) {
      t7 = fieldRowId(input.id);
      $[21] = input.id;
      $[22] = t7;
    } else {
      t7 = $[22];
    }
    let t8;
    if ($[23] === Symbol.for("react.memo_cache_sentinel")) {
      t8 = <span className="shrink-0 text-label-2 text-tertiary">inputs.</span>;
      $[23] = t8;
    } else {
      t8 = $[23];
    }
    const t9 = `min-w-0 px-1 py-0.5 font-medium! text-snippet-1 opacity-100! ${keyError ? "text-error" : "text-primary"}`;
    let t10;
    if ($[24] !== t) {
      t10 = t(_temp2);
      $[24] = t;
      $[25] = t10;
    } else {
      t10 = $[25];
    }
    let t11;
    if ($[26] !== input.id || $[27] !== onCommit) {
      t11 = next => onCommit(input.id, {
        name: next.trim()
      });
      $[26] = input.id;
      $[27] = onCommit;
      $[28] = t11;
    } else {
      t11 = $[28];
    }
    let t12;
    if ($[29] !== t) {
      t12 = t(_temp3);
      $[29] = t;
      $[30] = t12;
    } else {
      t12 = $[30];
    }
    let t13;
    if ($[31] !== autoEdit || $[32] !== canEdit || $[33] !== input.name || $[34] !== t10 || $[35] !== t11 || $[36] !== t12 || $[37] !== t9) {
      t13 = <span className="flex min-w-0 items-baseline gap-0.5 max-md:w-full">{t8}<span className="min-w-0 rounded-sm transition-colors hover:bg-hover in-data-force-affordance:bg-hover max-md:break-all"><EditableText as="div" autoFocus={autoEdit} className={t9} editable={canEdit} label={t10} onSubmit={t11} placeholder={t12} registry={inputMarkup} value={input.name} /></span></span>;
      $[31] = autoEdit;
      $[32] = canEdit;
      $[33] = input.name;
      $[34] = t10;
      $[35] = t11;
      $[36] = t12;
      $[37] = t9;
      $[38] = t13;
    } else {
      t13 = $[38];
    }
    const t14 = canEdit ? <InputTypeSelect ariaLabel={t(_temp4)} canEdit={true} id={`input-${input.id}-type`} onChange={updateSchema} schema={schemaState} /> : <TypeChip>{type ? FIELD_TYPE_BADGE[type] : t(_temp5)}</TypeChip>;
    const t15 = !canEdit;
    const t16 = `input-${input.id}-required`;
    let t17;
    if ($[39] !== input.id || $[40] !== onUpdate) {
      t17 = value => onUpdate(input.id, {
        required: value === "required"
      });
      $[39] = input.id;
      $[40] = onUpdate;
      $[41] = t17;
    } else {
      t17 = $[41];
    }
    const t18 = isMobile ? "default" : "compact";
    const t19 = input.required ? "required" : "optional";
    let t20;
    if ($[42] !== t15 || $[43] !== t16 || $[44] !== t17 || $[45] !== t18 || $[46] !== t19) {
      t20 = <RequiredToggle disabled={t15} id={t16} onOptionChange={t17} size={t18} value={t19} />;
      $[42] = t15;
      $[43] = t16;
      $[44] = t17;
      $[45] = t18;
      $[46] = t19;
      $[47] = t20;
    } else {
      t20 = $[47];
    }
    let t21;
    if ($[48] !== canEdit || $[49] !== input.id || $[50] !== onRemove || $[51] !== t) {
      t21 = canEdit && <span className="ml-auto flex shrink-0 items-center transition-opacity md:opacity-0 group-hover/input:opacity-100 in-data-force-affordance:opacity-100"><RemoveButton label={t(_temp6)} onClick={() => onRemove(input.id)} /></span>;
      $[48] = canEdit;
      $[49] = input.id;
      $[50] = onRemove;
      $[51] = t;
      $[52] = t21;
    } else {
      t21 = $[52];
    }
    let t22;
    if ($[53] !== t14 || $[54] !== t20 || $[55] !== t21) {
      t22 = <div className="contents max-md:flex max-md:w-full max-md:items-center max-md:gap-2.5">{t14}{t20}{t21}</div>;
      $[53] = t14;
      $[54] = t20;
      $[55] = t21;
      $[56] = t22;
    } else {
      t22 = $[56];
    }
    let t23;
    if ($[57] !== t13 || $[58] !== t22) {
      t23 = <div className="flex items-center gap-2.5 px-2 max-md:flex-wrap">{t13}{t22}</div>;
      $[57] = t13;
      $[58] = t22;
      $[59] = t23;
    } else {
      t23 = $[59];
    }
    let t24;
    if ($[60] !== keyError) {
      t24 = keyError && <div className="px-2"><FieldKeyErrorMessage error={keyError} /></div>;
      $[60] = keyError;
      $[61] = t24;
    } else {
      t24 = $[61];
    }
    let t25;
    if ($[62] !== t) {
      t25 = t(_temp7);
      $[62] = t;
      $[63] = t25;
    } else {
      t25 = $[63];
    }
    let t26;
    if ($[64] !== input.id || $[65] !== onCommit) {
      t26 = next_0 => onCommit(input.id, {
        description: next_0
      });
      $[64] = input.id;
      $[65] = onCommit;
      $[66] = t26;
    } else {
      t26 = $[66];
    }
    let t27;
    if ($[67] !== t) {
      t27 = t(_temp8);
      $[67] = t;
      $[68] = t27;
    } else {
      t27 = $[68];
    }
    let t28;
    if ($[69] !== canEdit || $[70] !== input.description || $[71] !== t25 || $[72] !== t26 || $[73] !== t27) {
      t28 = <EditableText as="div" block={true} className="min-h-7 rounded-sm px-2 py-1 text-body-2 text-primary opacity-100! transition-colors hover:bg-hover in-data-force-affordance:bg-hover [&:empty]:text-quaternary" editable={canEdit} label={t25} multiline={true} onSubmit={t26} optional={true} placeholder={t27} registry={inputMarkup} value={input.description} />;
      $[69] = canEdit;
      $[70] = input.description;
      $[71] = t25;
      $[72] = t26;
      $[73] = t27;
      $[74] = t28;
    } else {
      t28 = $[74];
    }
    let t29;
    if ($[75] !== t23 || $[76] !== t24 || $[77] !== t28) {
      t29 = <div className="flex flex-col gap-3">{t23}{t24}{t28}</div>;
      $[75] = t23;
      $[76] = t24;
      $[77] = t28;
      $[78] = t29;
    } else {
      t29 = $[78];
    }
    t3 = <Card.Body as="section" aria-label={named} data-id={t7} className="group/input flex flex-col gap-4 px-2 py-3">{t29}{isEnumType(type) && (canEdit || (input.enumValues ?? []).length > 0) && <OutputDetail header={<OutputColumns left={tTable(_temp9)} />}><EnumValues editable={canEdit} onChange={enumValues => updateSchema({
          enumValues
        })} values={padEnumValues(input.enumValues ?? undefined)} /></OutputDetail>}{isObjectType(type) && <OutputDetail header={properties.length === 0 ? <p className="px-2 text-body-3 text-tertiary">{type === "array<object>" ? t(_temp10) : t(_temp11)}</p> : <PropertyColumns description={tTable(_temp12)} name={tTable(_temp13)} type={tTable(_temp14)} />}><PropertyRows editable={canEdit} onChange={children => updateSchema({
          children
        })} properties={properties} requireProperties={false} /></OutputDetail>}</Card.Body>;
    $[2] = autoEdit;
    $[3] = canEdit;
    $[4] = input;
    $[5] = isMobile;
    $[6] = keyError;
    $[7] = onCommit;
    $[8] = onRemove;
    $[9] = onUpdate;
    $[10] = t;
    $[11] = tTable;
    $[12] = t3;
  } else {
    t3 = $[12];
  }
  return t3;
};
export default InputPane;
function _temp($) {
  return $.namePlaceholder;
}
function _temp2($_0) {
  return $_0.nameAria;
}
function _temp3($_1) {
  return $_1.namePlaceholder;
}
function _temp4($_2) {
  return $_2.typeAria;
}
function _temp5($_3) {
  return $_3.typeAria;
}
function _temp6($_4) {
  return $_4.removeAria;
}
function _temp7($_5) {
  return $_5.descriptionAria;
}
function _temp8($_6) {
  return $_6.descriptionPlaceholder;
}
function _temp9($_7) {
  return $_7.columns.values;
}
function _temp10($_8) {
  return $_8.openList;
}
function _temp11($_9) {
  return $_9.openObject;
}
function _temp12($_10) {
  return $_10.columns.description;
}
function _temp13($_11) {
  return $_11.columns.key;
}
function _temp14($_12) {
  return $_12.columns.type;
}